import { useSessionStorage } from "./useSessionStorage.js";
import { usePrevious } from "./usePrevious.js";
import { useEffect, useRef, useState } from "react";
import { isNull } from "lodash-es";
// src/hooks/useUrlState.ts
var defaultOpts = Object.freeze({
	removeOnUnmount: true,
	sessionStorage: false
});
function getDefaultValueType(defaultValue, dataType) {
	let defaultValueType = "string";
	if (Array.isArray(defaultValue) || dataType === "array") defaultValueType = "array";
	if (typeof defaultValue === "object" || dataType === "object") defaultValueType = "object";
	if (typeof defaultValue === "number" || dataType === "number") defaultValueType = "number";
	if (typeof defaultValue === "boolean" || dataType === "boolean") defaultValueType = "boolean";
	return defaultValueType;
}
function parseUrlValue(value, type) {
	switch (type) {
		case "number":
			if (typeof value === "undefined") return 0;
			return +value;
		case "string":
			if (typeof value === "undefined") return "";
			return value.toString();
		case "object":
			if (typeof value === "undefined") return {};
			return JSON.parse(value);
		case "array":
			if (typeof value === "undefined") return [];
			return JSON.parse(value);
		case "boolean":
			if (typeof value === "undefined") return false;
			return JSON.parse(value);
		default: return value;
	}
}
function makeUrlSafeValue(value) {
	if (typeof value !== "undefined") {
		if (Array.isArray(value) || typeof value === "object") return JSON.stringify(value);
		if (value?.toString) return value.toString();
	}
	return "";
}
/**
* Store and retrieve state from the URL search params.
*
* @param urlParamKey {string} The url search param key
* @param defaultValue {array|object|number|string} This value is used to coerce the data-type that is returned, ie: Array or Number. Default return value will be a string if not provided
* @param opts {Opts}
*/
function useUrlState(urlParamKey, defaultValue, opts = defaultOpts) {
	const mergedOpts = {
		...defaultOpts,
		...opts
	};
	const [sessionValue, setSessionValue] = useSessionStorage(urlParamKey);
	const [value, setValue] = useState(() => {
		const defaultValueType = getDefaultValueType(defaultValue, opts?.dataType);
		const initialUrlValue = new URLSearchParams(window?.location?.search || "").get(urlParamKey);
		if (typeof initialUrlValue !== "undefined" && !isNull(initialUrlValue)) return parseUrlValue(initialUrlValue, defaultValueType);
		if (mergedOpts.sessionStorage && typeof sessionValue !== "undefined" && !isNull(sessionValue)) return sessionValue;
		return defaultValue;
	});
	const local = useRef({ value });
	local.current.value = value;
	const prevValue = usePrevious(value);
	useEffect(() => {
		if (!urlParamKey || typeof urlParamKey !== "string") throw new Error("useUrlState() must have a \"urlParamKey\" prop as a string");
	}, [urlParamKey]);
	useEffect(() => {
		if (value === prevValue) return;
		const params = new URLSearchParams(window?.location?.search || "");
		if (typeof value !== "undefined" && !isNull(value)) params.set(urlParamKey, makeUrlSafeValue(value));
		else params.delete(urlParamKey);
		const searchStr = params.toString() ? `?${params.toString()}` : "";
		window?.history?.replaceState("", "", `${window.location.pathname}${searchStr}`);
		if (mergedOpts.sessionStorage) setSessionValue(value);
		window.dispatchEvent(new CustomEvent(`urlparam_${urlParamKey}`, { detail: { urlParamKey } }));
	}, [value]);
	useEffect(() => {
		const handler = () => {
			const initialUrlValue = new URLSearchParams(window?.location?.search || "").get(urlParamKey);
			let urlVal;
			if (typeof initialUrlValue !== "undefined" && !isNull(initialUrlValue)) urlVal = parseUrlValue(initialUrlValue, getDefaultValueType(defaultValue, opts?.dataType));
			if (JSON.stringify(urlVal) !== JSON.stringify(local.current.value)) setValue(urlVal);
		};
		addEventListener(`urlparam_${urlParamKey}`, handler);
		return () => {
			removeEventListener(`urlparam_${urlParamKey}`, handler);
			if (mergedOpts.removeOnUnmount) {
				const params = new URLSearchParams(window?.location?.search || "");
				params.delete(urlParamKey);
				const searchStr = params.toString() ? `?${params.toString()}` : "";
				window?.history?.replaceState("", "", `${window.location.pathname}${searchStr}`);
			}
		};
	}, []);
	return [value, setValue];
}
export { useUrlState };

//# sourceMappingURL=useUrlState.js.map